import { Fragment, useMemo, useState } from 'react'

import { cartLineKey } from '../cart/cartLineKey'
import { groupCartLines } from '../cart/groupCartLines'
import type { CartLine } from '../cart/types'
import { DataTable } from '../components/DataTable'
import { formatUnitLabel } from '../utils/formatUnitLabel'

type SupplierTitles = Record<number, string>

type RfqDraftTreeTableProps = {
  lines: CartLine[]
  supplierTitles: SupplierTitles
  onSupplierTitleChange: (supplierOrganizationId: number, title: string) => void
  onQuantityChange: (key: string, quantity: number) => void
  onNoteChange: (key: string, note: string) => void
  onRemoveLine: (key: string) => void
}

const collapseKey = (supplierId: number, productTypeId?: number) =>
  productTypeId == null ? `s:${supplierId}` : `s:${supplierId}:c:${productTypeId}`

export const RfqDraftTreeTable = ({
  lines,
  supplierTitles,
  onSupplierTitleChange,
  onQuantityChange,
  onNoteChange,
  onRemoveLine,
}: RfqDraftTreeTableProps) => {
  const groups = useMemo(() => groupCartLines(lines).inProvisium, [lines])
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const toggleCollapsed = (key: string) => {
    setCollapsed((current) => ({ ...current, [key]: !current[key] }))
  }

  const isCollapsed = (key: string) => collapsed[key] === true

  if (groups.length === 0) {
    return null
  }

  return (
    <DataTable
      wrapClassName="market-table-wrap--cards"
      className="market-rfq-tree market-table--cards"
      caption="Позиции заявки"
    >
        <thead>
          <tr>
            <th>Позиция</th>
            <th>Цена</th>
            <th>Кол-во</th>
            <th>Ед.</th>
            <th>Примечание</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {groups.map((supplier) => {
            const supplierId = supplier.supplierOrganizationId ?? 0
            const supplierCollapseKey = collapseKey(supplierId)
            const supplierCollapsed = isCollapsed(supplierCollapseKey)

            return (
              <Fragment key={supplierCollapseKey}>
                <tr className="market-rfq-tree__row market-rfq-tree__row--supplier">
                  <td colSpan={4}>
                    <button
                      type="button"
                      className="market-rfq-tree__toggle"
                      aria-expanded={!supplierCollapsed}
                      onClick={() => toggleCollapsed(supplierCollapseKey)}
                    >
                      {supplierCollapsed ? '▶' : '▼'} {supplier.supplierOrganizationName}
                    </button>
                    <span className="market-muted"> · {supplier.lineCount} поз.</span>
                  </td>
                  <td colSpan={2}>
                    <input
                      className="echat-field__input"
                      value={supplierTitles[supplierId] ?? ''}
                      placeholder="Название заявки"
                      onChange={(event) => onSupplierTitleChange(supplierId, event.target.value)}
                    />
                  </td>
                </tr>

                {!supplierCollapsed
                  ? supplier.categories.map((category) => {
                      const categoryCollapseKey = collapseKey(supplierId, category.productTypeId)
                      const categoryCollapsed = isCollapsed(categoryCollapseKey)

                      return (
                        <Fragment key={categoryCollapseKey}>
                          <tr className="market-rfq-tree__row market-rfq-tree__row--category">
                            <td colSpan={6}>
                              <button
                                type="button"
                                className="market-rfq-tree__toggle"
                                aria-expanded={!categoryCollapsed}
                                onClick={() => toggleCollapsed(categoryCollapseKey)}
                              >
                                {categoryCollapsed ? '▶' : '▼'} {category.productTypeName}
                              </button>
                              <span className="market-muted"> · {category.lines.length} поз.</span>
                            </td>
                          </tr>

                          {!categoryCollapsed
                            ? category.lines.map((line) => {
                                const key = cartLineKey(line)
                                return (
                                  <tr key={key} className="market-rfq-tree__row market-rfq-tree__row--line">
                                    <td>
                                      <div>{line.itemName}</div>
                                      {line.brand ? <div className="market-muted">{line.brand}</div> : null}
                                    </td>
                                    <td>{line.price ?? '—'}</td>
                                    <td>
                                      <input
                                        className="echat-field__input market-table__qty-input"
                                        inputMode="decimal"
                                        value={line.quantity}
                                        onChange={(event) =>
                                          onQuantityChange(key, Number(event.target.value))
                                        }
                                      />
                                    </td>
                                    <td>{formatUnitLabel(line.unit)}</td>
                                    <td>
                                      <input
                                        className="echat-field__input"
                                        value={line.note}
                                        placeholder={line.itemName}
                                        onChange={(event) => onNoteChange(key, event.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="echat-btn echat-btn--ghost echat-btn--sm"
                                        onClick={() => onRemoveLine(key)}
                                      >
                                        Удалить
                                      </button>
                                    </td>
                                  </tr>
                                )
                              })
                            : null}
                        </Fragment>
                      )
                    })
                  : null}
              </Fragment>
            )
          })}
        </tbody>
    </DataTable>
  )
}
